import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCompanies, fetchCompanyLevels, deleteCompanies } from '../api';
import type { CompanyQueryParams } from '../types';
import { useCurrentCompany } from '@/hooks/useCurrentCompany';
import { localizedReferenceDataKeys } from '@/locales/localizedReferenceData';
import { useAppLocale } from '@/locales/useAppLocale';

export const companiesKeys = {
  all: ['companies'] as const,
  lists: () => [...companiesKeys.all, 'list'] as const,
  list: (params: CompanyQueryParams) =>
    [...companiesKeys.lists(), params] as const,
  companyLevels: () => localizedReferenceDataKeys.companyLevels,
  companyLevelsList: (companyId: string, locale: string) =>
    [...companiesKeys.companyLevels(), companyId, locale] as const,
};

// Callers that mount the list behind a closed dialog pass enabled: false to skip the fetch.
export function useCompanies(params: CompanyQueryParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: companiesKeys.list(params),
    queryFn: () => fetchCompanies(params),
    enabled,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useCompanyLevels(companyId: string | null = null) {
  const currentCompany = useCurrentCompany();
  const locale = useAppLocale();
  const finalCompanyId = companyId || currentCompany.companyId;

  return useQuery({
    queryKey: companiesKeys.companyLevelsList(finalCompanyId ?? '', locale),
    queryFn: () => fetchCompanyLevels(finalCompanyId ?? '', locale),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!finalCompanyId,
  });
}

export function useDeleteCompanies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyIds: string[]) => deleteCompanies(companyIds),
    onSuccess: () => {
      // Invalidate only company list queries for more precise cache management
      queryClient.invalidateQueries({ queryKey: companiesKeys.lists() });
    },
  });
}
